import { z } from 'zod'
import { ExecutableRequest } from '@/types/api'
import { SorterOrder } from '@/types/api/sorter'
import { listResourceSchema } from '@/types/schema/api/list-resource-schema'
import { Owner } from '@/types/schema/api/owner-schema'
import { CollaborativeItemType } from '@/types/schema/collaborative-item'
import { PeriodType } from '@/types/schema/period-schema'
import { platformShortcodeSchema } from '@/types/schema/platform/platform-shortcode-schema'
import {
  PredictiveReport,
  PredictiveReportCandidate,
  PredictiveReportCollaborativeItem,
  PredictiveReportCollaborativeItemMetaData,
  PredictiveReportPlatformShortcode,
  predictiveReportCandidatePlatformAnalysisSchema,
  predictiveReportCandidateSchema,
  predictiveReportCollaborativeItemSchema,
  predictiveReportConfigSchema,
  predictiveReportSchema,
  predictiveReportSubtotalSchema,
} from '@/types/schema/predictive-report'

export interface PredictiveReportListRequest {
  page: number
  name: string
  filter_kol_uuid?: string
  filter_owner_id?: Owner['id']
  sort_by?: SorterOrder<keyof PredictiveReport>
}
export const predictiveReportListResponseSchema = listResourceSchema.extend({
  data: predictiveReportSchema.array(),
})
export type PredictiveReportListResponse = z.infer<
  typeof predictiveReportListResponseSchema
>

export type PredictiveReportConfigRequest = PredictiveReport['id']
export const predictiveReportConfigResponseSchema = predictiveReportConfigSchema
export type PredictiveReportConfigResponse = z.infer<
  typeof predictiveReportConfigResponseSchema
>

// 這個格式是暫時的，後端說有空要統一成 metadata 的格式
export type PredictiveReportRequestPlatformInfo = Record<
  CollaborativeItemType,
  boolean | undefined
>
export interface CreatePredictiveReportRequest {
  name: string
  owner_id: Owner['id']
  customized_tags_names: string[]
  ig: PredictiveReportRequestPlatformInfo
  fb: PredictiveReportRequestPlatformInfo
  yt: PredictiveReportRequestPlatformInfo
  start_date: string
  end_date: string
  total_budget: number
}
export const createPredictiveReportResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type CreatePredictiveReportResponse = z.infer<
  typeof createPredictiveReportResponseSchema
>

export interface EditPredictiveReportRequest
  extends CreatePredictiveReportRequest {
  id: PredictiveReport['id']
}
export const editPredictiveReportResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type EditPredictiveReportResponse = z.infer<
  typeof editPredictiveReportResponseSchema
>

export interface DuplicatePredictiveReportRequest {
  id: PredictiveReport['id']
  name: string
}
export const duplicatePredictiveReportResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type DuplicatePredictiveReportResponse = z.infer<
  typeof duplicatePredictiveReportResponseSchema
>

export type RemovePredictiveReportRequest = PredictiveReport['id']
export const removePredictiveReportResponseSchema = z.void()
export type RemovePredictiveReportResponse = z.infer<
  typeof removePredictiveReportResponseSchema
>

export type PredictiveReportSubtotalRequest = PredictiveReport['id']
export const predictiveReportSubtotalResponseSchema =
  predictiveReportSubtotalSchema
export type PredictiveReportSubtotalResponse = z.infer<
  typeof predictiveReportSubtotalResponseSchema
>

export interface PredictiveReportCandidateListRequest
  extends ExecutableRequest<
    PredictiveReportCandidateListRequest,
    PredictiveReportCandidateListResponse
  > {
  id: PredictiveReport['id']
  platform?: PredictiveReportPlatformShortcode
  selected?: boolean
  m3m6: PeriodType
  page: number
  per_page?: number
  sort_by?: SorterOrder<keyof PredictiveReportCandidate['kpi']>
}
export const predictiveReportCandidateListResponseSchema =
  listResourceSchema.extend({
    data: z.object({
      total_selected: z.number(),
      list: predictiveReportCandidateSchema.array(),
    }),
  })
export type PredictiveReportCandidateListResponse = z.infer<
  typeof predictiveReportCandidateListResponseSchema
>

export type PredictiveReportCandidateBasicInfoListRequest = {
  id: PredictiveReport['id']
  platform?: PredictiveReportPlatformShortcode
  filter_name?: string
  filter_kol_uuid?: string
  is_selected?: boolean
}
export const predictiveReportCandidateBasicInfoResponseSchema = z.object({
  kols: z
    .object({
      kol_uuid: z.string().uuid(),
      platforms: platformShortcodeSchema.array(),
      name: z.string(),
    })
    .array(),
})
export type PredictiveReportCandidateBasicInfoListResponse = z.infer<
  typeof predictiveReportCandidateBasicInfoResponseSchema
>

export interface AddPredictiveReportCandidateListRequest {
  id: PredictiveReport['id']
  uuids: PredictiveReportCandidate['uuid'][]
  collaborative_items: PredictiveReportCollaborativeItemMetaData[]
}
export const addPredictiveReportCandidateListResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type AddPredictiveReportCandidateListResponse = z.infer<
  typeof addPredictiveReportCandidateListResponseSchema
>

export interface EditPredictiveReportCandidateSelectStatusRequest {
  id: PredictiveReport['id']
  predictive_report_kol_id: PredictiveReportCandidate['predictive_report_kol_id']
  select: PredictiveReportCandidate['is_selected']
}
export const editPredictiveReportCandidateSelectStatusResponseSchema = z.object(
  {
    id: predictiveReportSchema.shape.id,
  },
)
export type EditPredictiveReportCandidateSelectStatusResponse = z.infer<
  typeof editPredictiveReportCandidateSelectStatusResponseSchema
>

export interface EditPredictiveReportCandidateNoteRequest {
  id: PredictiveReport['id']
  predictive_report_kol_id: PredictiveReportCandidate['predictive_report_kol_id']
  note: string
}
export const editPredictiveReportCandidateNoteResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type EditPredictiveReportCandidateNoteResponse = z.infer<
  typeof editPredictiveReportCandidateNoteResponseSchema
>

export interface EditPredictiveReportCandidateWillingnessToCooperateRequest {
  id: PredictiveReport['id']
  predictive_report_kol_id: PredictiveReportCandidate['predictive_report_kol_id']
  willingness_to_collaborate: PredictiveReportCandidate['willingness_to_collaborate']
}
export const editPredictiveReportCandidateWillingnessToCooperateResponseSchema =
  z.object({
    id: predictiveReportSchema.shape.id,
  })
export type EditPredictiveReportCandidateWillingnessToCooperateResponse =
  z.infer<
    typeof editPredictiveReportCandidateWillingnessToCooperateResponseSchema
  >

export interface EditPredictiveReportCandidateAdvertorialContentListRequest {
  id: PredictiveReport['id']
  predictive_report_kol_id: PredictiveReportCandidate['predictive_report_kol_id']
  post_ids: PredictiveReportCandidate['posts'][number]['post_id'][]
}
export const editPredictiveReportCandidateAdvertorialContentListResponseSchema =
  z.object({
    posts: predictiveReportCandidateSchema.shape.posts,
  })
export type EditPredictiveReportCandidateAdvertorialContentListResponse =
  z.infer<
    typeof editPredictiveReportCandidateAdvertorialContentListResponseSchema
  >

export interface PredictiveReportCollaborativeItemsRequest {
  id: PredictiveReport['id']
  uuid?: PredictiveReportCandidate['uuid']
  custom_data?: boolean
}
export const predictiveReportCollaborativeItemsResponseSchema = z.object({
  kol_collaborative_items: predictiveReportCollaborativeItemSchema.array(),
})
export type PredictiveReportCollaborativeItemsResponse = z.infer<
  typeof predictiveReportCollaborativeItemsResponseSchema
>

export interface EditPredictiveReportCandidateCollaborativeItemsRequest {
  id: PredictiveReport['id']
  uuid: PredictiveReportCandidate['uuid']
  kol_collaborative_items: PredictiveReportCollaborativeItem[]
}
export const editPredictiveReportCandidateCollaborativeItemsResponseSchema =
  z.object({
    id: predictiveReportSchema.shape.id,
  })
export type EditPredictiveReportCandidateCollaborativeItemsResponse = z.infer<
  typeof editPredictiveReportCandidateCollaborativeItemsResponseSchema
>

export interface RemovePredictiveReportCandidatePlatformRequest {
  id: PredictiveReport['id']
  predictive_report_kol_id: PredictiveReportCandidate['predictive_report_kol_id']
}
export const removePredictiveReportCandidatePlatformResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type RemovePredictiveReportCandidatePlatformResponse = z.infer<
  typeof removePredictiveReportCandidatePlatformResponseSchema
>

export type PredictiveReportDetailReportSubtotalRequest = PredictiveReport['id']
export const predictiveReportDetailReportSubtotalResponseSchema = z.object({
  estimate_value: z.object({
    budget: z.number(),
    cost: z.number(),
    cpe: z.number(),
    cpv: z.number(),
    cpev: z.number(),
    womm: z.number(),
  }),
  estimate_effectiveness: z.object({
    like_count: z.number(),
    comment_count: z.number(),
    share_count: z.number(),
    engagement_count: z.number(),
    view_count: z.number(),
    reach_count: z.number(),
    click_count: z.number(),
  }),
  summary: z.string().array(),
})
export type PredictiveReportDetailReportSubtotalResponse = z.infer<
  typeof predictiveReportDetailReportSubtotalResponseSchema
>

export interface PredictiveReportDetailReportPlatformSubtotalRequest {
  id: PredictiveReport['id']
  platform: PredictiveReportPlatformShortcode
  collaborative_items: PredictiveReportCollaborativeItemMetaData[]
  kol_uuids: PredictiveReportCandidate['uuid'][]
}
export const predictiveReportDetailReportPlatformSubtotalResponseSchema =
  z.object({
    estimate_value: z.object({
      cpe: z.number(),
      cpv: z.number(),
      cpev: z.number(),
      womm: z.number(),
    }),
    estimate_effectiveness: z.object({
      like_count: z.number(),
      click_count: z.number(),
      comment_count: z.number(),
      share_count: z.number(),
      engagement_count: z.number(),
      view_count: z.number(),
      reach_count: z.number(),
      engagement_rate: z.number(),
      view_rate: z.number(),
      reach_rate: z.number(),
    }),
  })
export type PredictiveReportDetailReportPlatformSubtotalResponse = z.infer<
  typeof predictiveReportDetailReportPlatformSubtotalResponseSchema
>

export interface PredictiveReportDetailReportKolsRequest {
  id: PredictiveReport['id']
  platform: PredictiveReportPlatformShortcode
  collaborative_items: PredictiveReportCollaborativeItemMetaData[]
  kol_uuids: PredictiveReportCandidate['uuid'][]
  page: number
  per_page: number
  sort_by?: SorterOrder<keyof PredictiveReportCandidate>
}
export const predictiveReportDetailReportKolsResponseSchema =
  listResourceSchema.extend({
    data: predictiveReportCandidatePlatformAnalysisSchema.array(),
  })
export type PredictiveReportDetailReportKolsResponse = z.infer<
  typeof predictiveReportDetailReportKolsResponseSchema
>

export type ExportPredictiveReportXlsxRequest = PredictiveReport['id']
export type ExportPredictiveReportXlsxResponse = ArrayBuffer

export type EditPredictiveReportCollaborativeItemsRequest = {
  id: PredictiveReport['id']
  kol_collaborative_items: PredictiveReportCollaborativeItem[]
}
export const editPredictiveReportCollaborativeItemsResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type EditPredictiveReportCollaborativeItemsResponse = z.infer<
  typeof editPredictiveReportCollaborativeItemsResponseSchema
>

export interface EditPredictiveReportCandidateCustomDataRequest {
  id: PredictiveReport['id']
  kol_collaborative_items: PredictiveReportCollaborativeItem[]
}
export const editPredictiveReportCandidateCustomDataResponseSchema = z.object({
  id: predictiveReportSchema.shape.id,
})
export type EditPredictiveReportCandidateCustomDataResponse = z.infer<
  typeof editPredictiveReportCandidateCustomDataResponseSchema
>

export type RefreshPredictiveReportDetailReportSummaryRequest =
  PredictiveReport['id']
export const refreshPredictiveReportDetailReportSummaryResponseSchema =
  z.undefined()
export type RefreshPredictiveReportDetailReportSummaryResponse = z.infer<
  typeof refreshPredictiveReportDetailReportSummaryResponseSchema
>

export type FetchPredictiveReportCollaborativeItemEstimatedPriceRequest = {
  id: PredictiveReport['id']
  uuid: PredictiveReportCandidate['uuid']
  platform: string
}
export const fetchPredictiveReportCollaborativeItemEstimatedPriceResponseSchema =
  z.object({
    ai_predicted_single_cooperation_fees: z
      .object({
        ad_authorization: z.number(),
        material_authorization: z.number(),
        uuid: z.string().uuid(),
        platform: z.string(),
        post: z.number().nullable(),
        video: z.number().nullable(),
      })
      .array(),
  })
export type FetchPredictiveReportCollaborativeItemEstimatedPriceResponse =
  z.infer<
    typeof fetchPredictiveReportCollaborativeItemEstimatedPriceResponseSchema
  >

export interface FetchPredictiveReportKolInvolvesRequest {
  kol_uuids: string[]
}
export const fetchPredictiveReportKolInvolvesResponseSchema = z.object({
  data: z.object({ uuid: z.string().uuid(), ids: z.number().array() }).array(),
})
export type FetchPredictiveReportKolInvolvesResponse = z.infer<
  typeof fetchPredictiveReportKolInvolvesResponseSchema
>
